import React, { useEffect, useState } from "react";
import { Alert, Modal, Button, Table, Form, Badge, Row, Col } from "react-bootstrap";
import {
  createOrganization,
  fetchOrganizations,
} from "../../utilities/AdminUtilities";
import LoadingComponent from "../Common/LoadingComponent";

function OrganizationDetails() {
  const [organizations, setOrganizations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAddLoading, setIsAddLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    organisation_name: "",
    organisation_domain: "",
    organisation_primary_contact: "",
    organisation_type: "",
    default_account_name: "",
    default_account_integration_type: "API",
    default_account_purpose: "",
    default_account_manager_email: "",
    details_object: {},
  });

  useEffect(() => {
    fetchOrganizations({ setOrganizations, name: searchName, setLoading });
  }, [searchName]);

  const handleAddOrganization = async () => {
    const newOrganization = {
      ...formData,
      creation_timestamp: new Date().toISOString(),
      organisation_status: "ACTIVE",
    };
    setErrorMessage("");
    try {
      const result = await createOrganization({
        reqdata: newOrganization,
        setLoading: setIsAddLoading,
      });
      if (!result?.success) {
        const detail = Array.isArray(result?.detail)
          ? result.detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join("; ")
          : result?.detail || result?.errorObject?.message;
        setErrorMessage(detail || "Could not create the organization.");
        return;
      }
      setShowAddModal(false);
      fetchOrganizations({ setOrganizations, name: searchName, setLoading });
    } catch (error) {
      setErrorMessage(error.message || "Could not create the organization.");
    }
  };

  const formatCreated = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? String(v)
      : d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const statusBadge = (status) => {
    if (!status) return "—";
    const positive = String(status).toUpperCase() === "ACTIVE";
    return (
      <Badge bg={positive ? "success" : "secondary"} pill>
        {status}
      </Badge>
    );
  };

  const OrganizationTable = () => (
    <div className="up-card">
      <Table hover responsive className="up-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Domain</th>
            <th>Primary Contact</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {organizations.length ? (
            organizations.map((org, index) => (
              <tr key={index}>
                <td>
                  <div className="up-person">
                    <span className="up-avatar">
                      {(org.organisation_name || "?")[0].toUpperCase()}
                    </span>
                    <span className="up-name">
                      {org.organisation_name || "—"}
                    </span>
                  </div>
                </td>
                <td>{org.organisation_domain || "—"}</td>
                <td className="up-email">
                  {org.organisation_primary_contact || "—"}
                </td>
                <td>{org.organisation_type || "—"}</td>
                <td>{statusBadge(org.organisation_status)}</td>
                <td className="up-date">
                  {formatCreated(org.creation_timestamp)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="up-empty">
                No organizations found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <>
      <div className="up-toolbar">
        <div className="up-filters">
          <Form.Control
            className="up-filter-wide"
            type="text"
            placeholder="Search by name"
            aria-label="Search by name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          {searchName && (
            <Button
              variant="link"
              className="up-clear"
              onClick={() => setSearchName("")}
            >
              Clear
            </Button>
          )}
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setErrorMessage("");
            setShowAddModal(true);
          }}
        >
          + Add Organization
        </Button>
      </div>
      {loading ? <LoadingComponent /> : <OrganizationTable />}

      <OrganizationForm
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        formData={formData}
        setFormData={setFormData}
        handleAddOrganization={handleAddOrganization}
        isAddLoading={isAddLoading}
        errorMessage={errorMessage}
      />
    </>
  );
}

export default OrganizationDetails;

const FORM_SECTIONS = [
  {
    title: "Organization",
    fields: [
      { key: "organisation_name", label: "Name", md: 6 },
      { key: "organisation_domain", label: "Domain", md: 6 },
      {
        key: "organisation_primary_contact",
        label: "Primary Contact",
        type: "email",
        md: 6,
      },
      { key: "organisation_type", label: "Type", md: 6 },
    ],
  },
  {
    title: "Default Account",
    fields: [
      { key: "default_account_name", label: "Name", md: 6 },
      { key: "default_account_purpose", label: "Purpose", md: 6 },
      {
        key: "default_account_integration_type",
        label: "Integration Type",
        md: 6,
        // Stored values stay API/EMBEDDED. EMBEDDED is required for partner
        // embed API keys (Partner API Keys tab); API is a regular customer account.
        options: [
          { value: "API", label: "Standard" },
          { value: "EMBEDDED", label: "Partner embed" },
        ],
      },
      {
        key: "default_account_manager_email",
        label: "Manager Email",
        type: "email",
        md: 6,
      },
    ],
  },
];

export const OrganizationForm = ({
  showAddModal,
  setShowAddModal,
  formData,
  setFormData,
  handleAddOrganization,
  isAddLoading,
  errorMessage,
}) => {
  return (
    <Modal
      show={showAddModal}
      onHide={() => setShowAddModal(false)}
      size="lg"
      centered
      scrollable
    >
      <Modal.Header closeButton className="pv-header">
        <div className="w-100">
          <div className="pv-eyebrow">New Organization</div>
          <div className="pv-title up-title text-break">
            {formData.organisation_name || "Untitled organization"}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {isAddLoading ? (
          <LoadingComponent />
        ) : (
          <Form>
            {FORM_SECTIONS.map((section) => (
              <React.Fragment key={section.title}>
                <div className="pv-section-title">{section.title}</div>
                <Row className="g-3">
                  {section.fields.map(({ key, label, type = "text", md, options }) => (
                    <Col md={md} key={key}>
                      <Form.Group className="ep-group">
                        <Form.Label className="pv-label">{label}</Form.Label>
                        {options ? (
                          <Form.Select
                            value={formData[key]}
                            onChange={(e) =>
                              setFormData({ ...formData, [key]: e.target.value })
                            }
                          >
                            {options.map(({ value, label: optionLabel }) => (
                              <option key={value} value={value}>
                                {optionLabel}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            type={type}
                            value={formData[key]}
                            onChange={(e) =>
                              setFormData({ ...formData, [key]: e.target.value })
                            }
                          />
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </React.Fragment>
            ))}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="ep-footer">
        <Button variant="outline-secondary" onClick={() => setShowAddModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleAddOrganization}>
          Create Organization
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
